import React, { memo, useState } from 'react';
import { CertificationServiceDropdown } from './CertificationServiceDropdown';
import { CACDropdown } from './CACDropdown';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';

export const BatchFormTable = memo(function BatchFormTable({
  fields,
  rows,
  services,
  selectedCells,
  isDragging,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  onPaste,
  onCellChange,
  onKeyDown,
  onRemoveRow,
  onAddRow
}) {
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const { handleKeyDown: handleArrowKeys, activeCell } = useKeyboardNavigation({ 
    rows, 
    fields,
    onNavigationStart: () => {
      onCellMouseUp(); // Clear selected cells
      setIsKeyboardMode(true);
    },
    onKeyDown
  });

  const handleMouseDown = (e, rowIndex, field) => {
    setIsKeyboardMode(false);
    onCellMouseDown(e, rowIndex, field);
  };

  return (
    <div 
      className="border border-gray-200 rounded-lg relative" 
      onMouseLeave={() => {
        if (isDragging) {
          onCellMouseUp();
        }
      }}
    >
      <div className="overflow-x-auto" style={{ maxHeight: '60vh' }}>
        <div className="pb-4">
        <table className="min-w-full divide-y divide-gray-200 border-collapse">
        <thead className="bg-gray-100">
          <tr>
            <th scope="col" className="w-8 px-2 py-1 sticky left-0 top-0 z-10 bg-gray-100 border border-gray-200 shadow-sm">
            </th>
            {fields.map(field => (
              <th 
                key={field.key}
                scope="col" 
                className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200 sticky top-0 z-10 bg-gray-100 shadow-sm"
                style={{
                  ...(field.key === 'certification_service_id' || field.key === 'certification_number' 
                      ? {
                          width: '110px',
                          minWidth: '110px',
                          maxWidth: '110px'
                        }
                      : {
                          minWidth: `${Math.max(field.label.length * 8, 120)}px` // Minimum width based on header
                        }
                  )
                }}
              >
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-2 py-1 whitespace-nowrap text-sm sticky left-0 bg-inherit border border-gray-200">
                <button
                  type="button"
                  onClick={() => onRemoveRow(rowIndex)}
                  className="text-red-600 hover:text-red-900 font-medium text-lg"
                >
                  ×
                </button>
              </td>
              {fields.map((field, fieldIndex) => (
                <td 
                  key={field.key} 
                  className={`p-0 text-sm ${
                    !isKeyboardMode && selectedCells.some(cell => 
                      cell.rowIndex === rowIndex && cell.field === field.key
                    ) ? 'border-2 border-solid border-blue-500' : 
                    activeCell?.rowIndex === rowIndex && activeCell?.fieldIndex === fieldIndex ? 
                    'border-2 border-solid border-blue-500' : 'border border-gray-200'
                  } ${isDragging ? 'select-none' : ''}`}
                  style={{
                    userSelect: isDragging ? 'none' : 'text',
                    cursor: isDragging ? 'cell' : 'default',
                    position: 'relative',
                    ...(field.key === 'certification_service_id' || field.key === 'certification_number'
                        ? {
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px'
                          }
                        : {
                            minWidth: `${Math.max(field.label.length * 8, 120)}px` // Minimum width based on header
                          }
                    )
                  }}
                  onMouseDown={(e) => handleMouseDown(e, rowIndex, field.key)}
                  onMouseEnter={() => onCellMouseEnter(rowIndex, field.key)}
                  onMouseUp={onCellMouseUp}
                  onPaste={(e) => {
                    onPaste(e, rowIndex, field.key);
                    e.preventDefault();
                  }}
                >
                  {field.type === 'images' ? (
                    <div 
                      className="flex gap-1 p-1 overflow-x-auto"
                      tabIndex={0}
                      name={field.key}
                      data-row={rowIndex}
                      data-field={field.key}
                      onKeyDown={(e) => {
                        handleArrowKeys(e, rowIndex, fieldIndex, fields);
                        if (['Tab', 'Enter'].includes(e.key)) {
                          onKeyDown(e, rowIndex, fieldIndex, fields);
                        }
                      }}
                    >
                      {row.images?.map((img, index) => (
                        <img 
                          key={index}
                          src={img.image_url}
                          alt={`Cert image ${index + 1}`}
                          className="h-8 w-auto object-contain"
                        />
                      ))}
                    </div>
                  ) : field.key === 'certification_service_id' ? (
                    <CertificationServiceDropdown
                      rowIndex={rowIndex}
                      value={row[field.key]}
                      services={services}
                      selectedCells={selectedCells}
                      onChange={(e) => onCellChange(rowIndex, field.key, e.target.value)}
                      onKeyDown={(e) => {
                        handleArrowKeys(e, rowIndex, fieldIndex, fields);
                        if (['Tab', 'Enter'].includes(e.key)) {
                          onKeyDown(e, rowIndex, fieldIndex, fields);
                        }
                      }}
                      fieldIndex={fieldIndex}
                    />
                  ) : field.type === 'select' ? (
                    field.key === 'cac_sticker' ? (
                      <CACDropdown
                        rowIndex={rowIndex}
                        value={row[field.key]}
                        onChange={(e) => onCellChange(rowIndex, field.key, e.target.value)}
                        onKeyDown={(e) => {
                          handleArrowKeys(e, rowIndex, fieldIndex, fields);
                          if (['Tab', 'Enter'].includes(e.key)) {
                            onKeyDown(e, rowIndex, fieldIndex, fields);
                          }
                        }}
                        fieldIndex={fieldIndex}
                        field={field}
                      />
                    ) : (
                    <select
                      value={row[field.key] || ''}
                      onChange={(e) => onCellChange(rowIndex, field.key, e.target.value)}
                      className="block w-full h-full px-2 py-1 text-sm border-0 outline-none focus:ring-0 focus:outline-none relative"
                      name={field.key}
                      data-row={rowIndex}
                      data-field={field.key}
                      onKeyDown={(e) => {
                        handleArrowKeys(e, rowIndex, fieldIndex, fields);
                        if (['Tab', 'Enter'].includes(e.key)) {
                          onKeyDown(e, rowIndex, fieldIndex, fields);
                        }
                      }}
                    >
                      {field.options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    )
                  ) : (
                    <input
                      type={field.type}
                      step={field.step}
                      value={row[field.key] || ''}
                      onChange={(e) => onCellChange(
                        rowIndex, 
                        field.key, 
                        field.type === 'number' ? e.target.value === '' ? '' : Number(e.target.value) : e.target.value
                      )}
                      className="block w-full h-full px-2 py-1 text-sm border-0 outline-none focus:ring-0 focus:outline-none relative break-words"
                      name={field.key}
                      autoComplete="off"
                      data-row={rowIndex}
                      data-field={field.key}
                      onKeyDown={(e) => {
                        handleArrowKeys(e, rowIndex, fieldIndex, fields);
                        if (['Tab', 'Enter'].includes(e.key)) {
                          onKeyDown(e, rowIndex, fieldIndex, fields);
                        }
                      }}
                      onPaste={(e) => {
                        onPaste(e, rowIndex, field.key);
                        e.preventDefault();
                      }}
                      placeholder={field.required && field.key !== 'year' && field.key !== 'denomination' ? 'Required' : ''}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if these props have changed
  return (
    prevProps.fields === nextProps.fields &&
    prevProps.rows === nextProps.rows &&
    prevProps.services === nextProps.services &&
    prevProps.selectedCells === nextProps.selectedCells &&
    prevProps.isDragging === nextProps.isDragging
  );
});
